import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function navLinkClass(isActive) {
  return isActive ? "btn-primary" : "btn-outline";
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isInstructorRoute = location.pathname === "/instructor";
  const [largePrint, setLargePrint] = useState(() => localStorage.getItem("academy-large-print") === "1");

  useEffect(() => {
    document.documentElement.classList.toggle("large-print", largePrint);
    localStorage.setItem("academy-large-print", largePrint ? "1" : "0");
  }, [largePrint]);

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <a
        href="#main-content"
        className="skip-link"
        data-testid="skip-link"
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById("main-content");
          if (main) main.focus();
        }}
      >
        Skip to main content
      </a>
      <header className="border-b border-border bg-card" data-testid="academy-header">
        <div className="mx-auto max-w-5xl px-4 py-4 flex flex-wrap items-center gap-4 justify-between">
          <Link to="/" className="text-xl font-bold text-primary" data-testid="brand-link">
            GDI Academy
          </Link>
          <nav className="flex items-center gap-3 flex-wrap" aria-label="Primary">
            <button
              type="button"
              onClick={() => setLargePrint((v) => !v)}
              aria-pressed={largePrint}
              className="btn-outline"
              data-testid="large-print-toggle"
            >
              {largePrint ? "Standard print" : "Large print"}
            </button>
            {user && (user.role === "instructor" || user.role === "admin") && (
              <Link
                to="/instructor"
                className={navLinkClass(isInstructorRoute)}
                data-testid="instructor-nav-link"
                aria-current={isInstructorRoute ? "page" : undefined}
              >
                Instructor
              </Link>
            )}
            {user && user.role === "admin" && (
              <Link to="/admin" className="btn-outline" data-testid="admin-nav-link">
                Admin
              </Link>
            )}
            {user ? (
              <>
                <Link
                  to="/theory"
                  className={navLinkClass(location.pathname === "/theory")}
                  data-testid="theory-nav-link"
                >
                  Theory Test Preparation
                </Link>
                <Link
                  to="/dashboard"
                  className={navLinkClass(location.pathname === "/dashboard")}
                  data-testid="dashboard-nav-link"
                >
                  Dashboard
                </Link>
                <button type="button" onClick={handleLogout} className="btn-primary" data-testid="logout-button">
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-outline" data-testid="nav-login-link">
                  Log in
                </Link>
                <Link to="/register" className="btn-accent" data-testid="nav-register-link">
                  Create Learner Account
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 mx-auto w-full max-w-5xl px-4 ${isInstructorRoute ? "py-4 pb-28" : "py-8"}`}
      >
        {children}
      </main>
      <footer className="border-t border-border bg-card">
        <div className="mx-auto max-w-5xl px-4 py-6 text-sm space-y-2 text-muted-foreground">
          <p data-testid="footer-disclaimer">
            Educational disclaimer: GDI Academy content supports learner driver education. It does not replace the
            official New Zealand Road Code or professional practical instruction.
          </p>
          <p data-testid="footer-privacy">
            Privacy notice: GDI Academy stores your email, display name, learning progress and optional lesson
            feedback for your learning account. See the{" "}
            <Link to="/privacy" className="underline">
              Privacy Notice
            </Link>{" "}
            and{" "}
            <Link to="/terms" className="underline">
              Terms
            </Link>
            . Contact{" "}
            <a className="underline" href="mailto:info@getdrivinginstructor.co.nz">
              info@getdrivinginstructor.co.nz
            </a>{" "}
            with privacy questions, account deletion requests, or data-retention queries.
          </p>
        </div>
      </footer>
    </div>
  );
}
